/**
 * Reconcile a persisted column order against the live column set.
 *
 * The persisted `order` (a full snapshot of column ids saved when the user drag-reorders) can
 * drift from what the table currently declares: a column may have been removed or renamed in a
 * later deploy, a new column may have been added, or the sticky-prefix invariant may no longer
 * hold. This pure function produces a valid order that:
 *
 *   1. drops ids no longer present in the column set (removed / renamed columns),
 *   2. keeps the persisted relative order of the columns that survive,
 *   3. appends newly-added ids (present in the defs but absent from the snapshot) after the
 *      persisted ones, in their natural definition order,
 *   4. forces the sticky columns to remain the leading prefix, in their declared order
 *      (the "sticky columns form a prefix of the visible order" invariant).
 *
 * Hidden columns are intentionally retained in the order (the snapshot is over ALL column ids,
 * not just visible ones) so that re-showing a column restores it at its remembered position.
 *
 * Returns a fresh array (never mutates inputs). When `persisted` is undefined/empty the result
 * is simply the natural order with the sticky prefix in front — i.e. the column-def default.
 *
 * @param persisted        persisted order snapshot, or undefined when nothing is saved
 * @param allIds           every column id in natural (definition) order
 * @param stickyPrefixIds  sticky columns in declared order (the intended leading prefix)
 */
export const reconcileColumnOrder = (
    persisted: readonly string[] | undefined,
    allIds: readonly string[],
    stickyPrefixIds: readonly string[]
): string[] => {
    const allSet = new Set(allIds);
    const stickySet = new Set(stickyPrefixIds);
    const placed = new Set<string>();
    const middle: string[] = [];

    // 1+2: take persisted ids that still exist and aren't sticky, preserving their order; drop
    // unknown ids and de-duplicate defensively.
    for (const id of persisted ?? []) {
        if (!allSet.has(id) || stickySet.has(id) || placed.has(id)) continue;
        placed.add(id);
        middle.push(id);
    }

    // 3: append columns that exist but weren't in the snapshot (newly added), in natural order.
    for (const id of allIds) {
        if (stickySet.has(id) || placed.has(id)) continue;
        placed.add(id);
        middle.push(id);
    }

    // 4: sticky columns lead, in their declared order; skip any that no longer exist.
    const prefix = stickyPrefixIds.filter((id) => allSet.has(id));

    return [...prefix, ...middle];
};
